import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { PurchaseOrderDetailAwardStatus } from '../../enums';
import { BaseEntity } from './base.entity';
import { PurchaseOrderReceiptDetailEntity } from './purchase-order-receipt-detail.entity';
import { PurchaseOrderEntity } from './purchase-order.entity';
import { ShipmentDetailEntity } from './shipment-detail.entity';
import type { SourcingRequestLineEntity } from './sourcing-request-line.entity';
import { VendorItemEntity } from './vendor-item.entity';

@Index(
	'purchase_order_detail__fk_purchase_order__id__deleted_userId',
	['deletedUserId', 'fkPurchaseOrderId'],
	{},
)
@Index(
	'purchase_order_detail__fk_purchase_order__id_fk',
	['fkPurchaseOrderId'],
	{},
)
@Index('purchase_order_detail__fk_vendor_item__id_fk', ['fkVendorItemId'], {})
@Index(
	'purchase_order_detail_externalId__deleted_userId',
	['externalId', 'deletedUserId'],
	{},
)
@Index(
	'purchase_order_detail__fk_sourcing_request_line__id_fk',
	['fkSourcingRequestLineId'],
	{},
)
@Entity('purchase_order_detail')
export class PurchaseOrderDetailEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', length: 255 })
	public externalId!: string;

	@Column('int', { name: 'quantityOrdered', nullable: true })
	public quantityOrdered!: number | null;

	@Column('int', { name: 'quantityReceived', nullable: true })
	public quantityReceived!: number | null;

	@Column('int', { name: 'quantityBackordered', nullable: true })
	public quantityBackordered!: number | null;

	@Column('decimal', { name: 'cost', nullable: true, precision: 15, scale: 6 })
	public cost!: string | null;

	@Column('datetime', { name: 'expectedReceiptDate', nullable: true })
	public expectedReceiptDate!: Date | null;

	@Column('int', { name: '_fk_purchase_order__id', unsigned: true })
	public fkPurchaseOrderId!: number;

	@Column('int', {
		name: '_fk_vendor_item__id',
		nullable: true,
		unsigned: true,
	})
	public fkVendorItemId!: number | null;

	@Column('decimal', {
		name: 'conversionFactor',
		precision: 11,
		scale: 4,
		default: () => "'1.0000'",
	})
	public conversionFactor!: string;

	/**
	 * Optional link to the SR line this PO detail represents (the
	 * line that asked for this item). Set during RFQ creation
	 * (`sendRfq` populates it from the encoded externalId regression
	 * point) and during award when cloning details to a new PO.
	 *
	 * This is the canonical SR ↔ PO mapping. The header-level FK
	 * `purchase_order.fk_sourcing_request__id` was removed because it
	 * forced a 1:1 SR-PO model that prevented consolidating multiple
	 * SRs onto a single vendor PO. PO-level "what SRs are touched"
	 * queries derive distinct SR ids from these line links.
	 *
	 * Nullable for direct-create POs not driven by an SR.
	 */
	@Column('int', {
		name: '_fk_sourcing_request_line__id',
		nullable: true,
		unsigned: true,
	})
	public fkSourcingRequestLineId!: number | null;

	/**
	 * Per-line award decision when this PO arrived through a multi-vendor
	 * RFQ flow. Default `pending` until the buyer awards the round (or for
	 * non-RFQ POs that get created directly). On award, winning lines flip
	 * to `awarded` and lose lines flip to `not_awarded` — the PO releases
	 * with both kinds of rows visible for audit, but receiving/fulfillment
	 * skip `not_awarded`. See docs/claude/sourcing-requests/RFQ-DESIGN.md.
	 */
	@Column('enum', {
		name: 'awardStatus',
		enum: PurchaseOrderDetailAwardStatus,
		default: PurchaseOrderDetailAwardStatus.PENDING,
	})
	public awardStatus!: PurchaseOrderDetailAwardStatus;

	@ManyToOne(
		() => PurchaseOrderEntity,
		(purchaseOrder) => purchaseOrder.purchaseOrderDetails,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([{ name: '_fk_purchase_order__id', referencedColumnName: 'id' }])
	public fkPurchaseOrder!: PurchaseOrderEntity;

	@ManyToOne(
		() => VendorItemEntity,
		(vendorItem) => vendorItem.purchaseOrderDetails,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([{ name: '_fk_vendor_item__id', referencedColumnName: 'id' }])
	public fkVendorItem!: VendorItemEntity;

	@ManyToOne('SourcingRequestLineEntity', {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: '_fk_sourcing_request_line__id', referencedColumnName: 'id' },
	])
	public fkSourcingRequestLine!: SourcingRequestLineEntity | null;

	@OneToMany(
		() => PurchaseOrderReceiptDetailEntity,
		(purchaseOrderReceiptDetail) =>
			purchaseOrderReceiptDetail.fkPurchaseOrderDetail,
		{},
	)
	public purchaseOrderReceiptDetails!: Array<PurchaseOrderReceiptDetailEntity>;

	@OneToMany(
		() => ShipmentDetailEntity,
		(shipmentDetail) => shipmentDetail.purchaseOrderDetail,
		{},
	)
	public shipmentDetails!: Array<ShipmentDetailEntity>;
}
